import React from "react";
import { cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CTooltip } from "@coreui/react";

// Only metrics with a good/bad meaning get rated. Return, final equity and time-underwater are
// relative to the period or the strategy style, so a traffic light on them would mislead.
// "drawdown" is rated against the trader's own limit, not a fixed band, so 30% is not universally red.
export type RatingKind = "risk_of_ruin" | "ends_below" | "drawdown";

type Rating = "good" | "warn" | "bad";

// Fixed-band metrics (lower is better), in percent. Drawdown is handled separately against the limit.
const BANDS: Record<"risk_of_ruin" | "ends_below", { warn: number; bad: number }> = {
  risk_of_ruin: { warn: 1, bad: 5 },
  ends_below: { warn: 10, bad: 30 },
};

// A drawdown is green comfortably under the limit, yellow as it approaches, red once it reaches it.
const DRAWDOWN_WARN_FRACTION = 0.8;

const RATING_CLASS: Record<Rating, string> = {
  good: "text-success",
  warn: "text-warning",
  bad: "text-danger",
};

const rate = (kind: RatingKind, valuePct: number, limitPct?: number): Rating => {
  if (kind === "drawdown") {
    const limit = limitPct ?? 30;
    if (valuePct >= limit) return "bad";
    if (valuePct >= limit * DRAWDOWN_WARN_FRACTION) return "warn";
    return "good";
  }
  const { warn, bad } = BANDS[kind];
  if (valuePct >= bad) return "bad";
  if (valuePct >= warn) return "warn";
  return "good";
};

const helpFor = (kind: RatingKind, limitPct?: number): string => {
  if (kind === "drawdown") {
    const limit = limitPct ?? 30;
    const warnAt = Math.round(limit * DRAWDOWN_WARN_FRACTION);
    return `Green under ${warnAt}%, yellow ${warnAt} to ${limit}%, red over your ${limit}% limit. Depends on your leverage and position sizing.`;
  }
  if (kind === "risk_of_ruin") {
    return "Green under 1%, yellow 1 to 5%, red over 5%. A system you fund with real money should sit near zero.";
  }
  return "Green under 10%, yellow 10 to 30%, red over 30%. A real edge finishes profitable in most runs.";
};

/**
 * Traffic-light info icon for a rated metric: the standard (i), colored green/yellow/red by how the
 * value scores. Hover explains the band. The value itself keeps its own color.
 */
export const McRatingDot: React.FC<{ kind: RatingKind; valuePct: number; limitPct?: number }> = ({
  kind,
  valuePct,
  limitPct,
}) => (
  <CTooltip content={helpFor(kind, limitPct)} placement="top">
    <CIcon
      className={`McRatingIcon ${RATING_CLASS[rate(kind, valuePct, limitPct)]}`}
      icon={cilInfo}
      size="sm"
    />
  </CTooltip>
);
